/**
 * <s-carousel> — progressive enhancement for a native scroll-snap row.
 *
 * Markup contract:
 *   [data-carousel-track] — the horizontally scrolling list (required).
 *   [data-carousel-prev] / [data-carousel-next] — optional step buttons.
 *   autoplay="ms" — optional; advances one slide at a time and loops.
 *
 * Without JavaScript the row still swipes and scrolls. Autoplay never runs
 * for reduced-motion users, and pauses while the row is hovered, focused,
 * touched, off-screen, or the tab is hidden.
 */
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class SCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    if (!this.track) {
      // A single video (no slider track) still gets visible-only autoplay.
      this.setupVideos();
      return;
    }

    this.prevButton = this.querySelector('[data-carousel-prev]');
    this.nextButton = this.querySelector('[data-carousel-next]');
    this.paused = false;
    this.visible = false;

    this.onPrev = () => this.step(-1);
    this.onNext = () => this.step(1);
    this.onScroll = () => this.updateButtons();
    this.onPause = () => { this.paused = true; };
    this.onResume = () => { this.paused = false; };

    this.prevButton?.addEventListener('click', this.onPrev);
    this.nextButton?.addEventListener('click', this.onNext);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.addEventListener('pointerenter', this.onPause);
    this.addEventListener('pointerleave', this.onResume);
    this.addEventListener('focusin', this.onPause);
    this.addEventListener('focusout', this.onResume);
    this.addEventListener('touchstart', this.onPause, { passive: true });

    this.updateButtons();
    this.startAutoplay();
    this.setupVideos();
  }

  /**
   * Videos inside [data-autoplay-video] play muted and inline only while at
   * least 60% visible, and pause when scrolled away. Viewers can unmute with
   * the native controls. Reduced-motion users get click-to-play only.
   */
  setupVideos() {
    const videos = [...this.querySelectorAll('[data-autoplay-video] video')];
    if (!videos.length) return;
    for (const video of videos) {
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
    }
    if (reducedMotion.matches) return;
    this.videoObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const video = entry.target;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
          video.play().catch(() => {});
        } else if (!video.paused) {
          video.pause();
        }
      }
    }, { threshold: [0, 0.6] });
    for (const video of videos) this.videoObserver.observe(video);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
    this.observer?.disconnect();
    this.videoObserver?.disconnect();
    this.prevButton?.removeEventListener('click', this.onPrev);
    this.nextButton?.removeEventListener('click', this.onNext);
    this.track?.removeEventListener('scroll', this.onScroll);
  }

  startAutoplay() {
    const interval = Number(this.getAttribute('autoplay'));
    if (!(interval > 0) || reducedMotion.matches) return;
    this.observer = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
    });
    this.observer.observe(this);
    this.timer = setInterval(() => {
      if (!this.paused && this.visible && !document.hidden) this.step(1, true);
    }, interval);
  }

  get maxScroll() {
    return this.track.scrollWidth - this.track.clientWidth;
  }

  get position() {
    return Math.abs(this.track.scrollLeft);
  }

  step(direction, loop = false) {
    const track = this.track;
    const behavior = reducedMotion.matches ? 'auto' : 'smooth';
    if (this.maxScroll <= 2) return;
    if (loop && direction > 0 && this.position >= this.maxScroll - 2) {
      track.scrollTo({ left: 0, behavior });
      return;
    }
    const slide = track.firstElementChild;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const distance = slide ? slide.getBoundingClientRect().width + gap : track.clientWidth;
    const sign = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
    track.scrollBy({ left: direction * sign * distance, behavior });
  }

  updateButtons() {
    const scrollable = this.maxScroll > 2;
    if (this.prevButton) this.prevButton.disabled = !scrollable || this.position <= 2;
    if (this.nextButton) this.nextButton.disabled = !scrollable || this.position >= this.maxScroll - 2;
  }
}

if (!customElements.get('s-carousel')) customElements.define('s-carousel', SCarousel);
